<template>
  <footer ref="root" class="footer">
    <MarqueeText class="footer__marquee" :items="['Gente coppada', 'Hecho en Rosario']" :speed="80" />

    <div class="footer__grid">
      <div class="footer__contact">
        <h2 class="h2 footer__title" data-reveal="lines">Let's get<br>in touch</h2>
        <p class="footer__lead" data-reveal>Leave us your email, let's get in touch.</p>
        <form class="footer__form" novalidate data-reveal @submit.prevent="sendEmail">
          <input
            v-model.trim="email"
            type="email"
            placeholder="your@email.com"
            autocomplete="email"
            aria-label="Your e-mail"
            class="footer__input"
            :disabled="status === 'sending'"
          >
          <button v-magnetic="0.3" type="submit" class="pill pill--solid footer__submit" :disabled="status === 'sending'">
            <span class="roll"><span data-text="Contact me">Contact me</span></span>
          </button>
        </form>
        <p class="footer__status" :class="`is-${status}`" aria-live="polite">{{ statusText }}</p>
      </div>

      <nav class="footer__col" aria-label="Secciones" data-reveal>
        <p class="eyebrow">Sitemap</p>
        <a v-for="link in sitemap" :key="link.label" :href="link.href" class="footer__link" @click.prevent="go(link)">
          <span class="roll"><span :data-text="link.label">{{ link.label }}</span></span>
        </a>
      </nav>

      <nav class="footer__col" aria-label="Redes" data-reveal>
        <p class="eyebrow">Follow</p>
        <a v-for="social in socials" :key="social.name" :href="social.url" target="_blank" rel="noopener" class="footer__link">
          <span class="roll"><span :data-text="social.name">{{ social.name }}</span></span>
          <span class="arrow" aria-hidden="true">↗</span>
        </a>
      </nav>
    </div>

    <div class="footer__wordmark" data-reveal="image">
      <img src="../assets/logotipo-short.webp" alt="Coppados">
    </div>

    <div class="footer__bottom">
      <span>&copy; Copyright {{ year }} COPPADOS. All rights reserved</span>
      <a href="https://seiminteractive.io" target="_blank" rel="noopener noreferrer">
        <span class="roll"><span data-text="Web design by SEIM INTERACTIVE">Web design by SEIM INTERACTIVE</span></span>
      </a>
      <button type="button" class="footer__top" @click="toTop">
        <span class="roll"><span data-text="Back to top ↑">Back to top ↑</span></span>
      </button>
    </div>
  </footer>
</template>

<script>
import { apiUrl } from '@/services/apiConfig'
import { scrollToTarget } from '@/animations/smoothScroll'
import motion from '@/mixins/motion'
import MarqueeText from '@/components/ui/MarqueeText.vue'

const EMAIL = /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/

export default {
  name: 'FooterInformation',
  components: { MarqueeText },
  mixins: [motion],
  data() {
    return {
      email: '',
      status: 'idle',
      year: new Date().getFullYear(),
      sitemap: [
        { label: 'Music', href: '/#music', hash: '#music' },
        { label: 'Events', href: '/#events', hash: '#events' },
        { label: 'Artists', href: '/#artists', hash: '#artists' },
        { label: 'Shop', href: '/shop', path: '/shop' }
      ],
      socials: [
        { name: 'Instagram', url: 'https://www.instagram.com/coppados/' },
        { name: 'Beatport', url: 'https://www.beatport.com/es/label/coppados/126440' },
        { name: 'SoundCloud', url: 'https://soundcloud.com/coppados/tracks' },
        { name: 'Spotify', url: 'https://open.spotify.com/playlist/1Zx2DXeOljqEk8bjJUWD3y' },
        { name: 'TikTok', url: 'https://www.tiktok.com/@coppados' },
        { name: 'YouTube', url: 'https://www.youtube.com/@coppados' }
      ]
    }
  },
  computed: {
    statusText() {
      if (this.status === 'invalid') return 'Please enter a valid e-mail.'
      if (this.status === 'error') return 'Mail could not be sent. Please try again.'
      if (this.status === 'sent') return 'Mail sent successfully!'
      return ''
    }
  },
  mounted() {
    this.revealContent()
  },
  methods: {
    go(link) {
      if (link.path) {
        if (this.$route.path !== link.path) this.$router.push(link.path)
        return
      }
      if (this.$route.path === '/') scrollToTarget(link.hash)
      else this.$router.push({ path: '/', hash: link.hash })
    },
    toTop() {
      scrollToTarget(document.body)
    },
    async sendEmail() {
      if (!EMAIL.test(this.email)) {
        this.status = 'invalid'
        return
      }
      this.status = 'sending'
      try {
        const response = await fetch(apiUrl('/send'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: 'bookings@coppados.com',
            message: `Hola, te contacto desde la web de COPPADOS. Mi email es: ${this.email}`
          })
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.status = 'sent'
        this.email = ''
      } catch (error) {
        console.error('Error sending contact mail:', error)
        this.status = 'error'
      }
    }
  }
}
</script>

<style scoped>
.footer {
  position: relative;
  z-index: 1;
  background: var(--c-bg);
  border-top: 1px solid var(--c-line);
  overflow: hidden;
}

.footer__marquee {
  font-size: clamp(3.5rem, 1rem + 10vw, 11rem);
  line-height: 1.15;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  padding: clamp(2rem, 1rem + 3vw, 4rem) 0;
  border-bottom: 1px solid var(--c-line);
}

.footer__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.7fr) minmax(0, 0.7fr);
  gap: 3rem;
  padding: clamp(3rem, 2rem + 4vw, 6rem) var(--gutter);
}

.footer__lead {
  margin-top: 1.25rem;
  color: var(--c-muted);
  font-size: var(--fs-body);
}

.footer__form {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  max-width: 34rem;
}

.footer__input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--c-line);
  padding: 0.9rem 0;
  color: #fff;
  font-size: var(--fs-lead);
  outline: none;
  transition: border-color 0.4s var(--ease-out);
}

.footer__input:focus {
  border-color: #fff;
}

.footer__input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.footer__submit {
  flex-shrink: 0;
}

.footer__submit[disabled] {
  opacity: 0.6;
}

.footer__status {
  min-height: 1.4em;
  margin-top: 0.9rem;
  font-size: 0.85rem;
  color: var(--c-muted);
}

.footer__status.is-sent {
  color: #fff;
}

.footer__status.is-error,
.footer__status.is-invalid {
  color: var(--c-accent);
}

.footer__col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.footer__col .eyebrow {
  margin-bottom: 0.75rem;
}

.footer__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(1rem, 0.9rem + 0.4vw, 1.3rem);
  text-transform: uppercase;
  --roll-color: var(--c-accent);
}

.footer__link .arrow {
  font-size: 0.75em;
  color: var(--c-muted);
}

.footer__wordmark {
  padding: 0 var(--gutter);
  overflow: hidden;
}

.footer__wordmark img {
  width: 100%;
  height: auto;
}

.footer__bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-top: clamp(2rem, 1rem + 2vw, 3rem);
  padding: 1.5rem var(--gutter);
  border-top: 1px solid var(--c-line);
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  color: var(--c-muted);
  --roll-color: #fff;
}

.footer__top {
  font-size: inherit;
  letter-spacing: inherit;
  color: inherit;
}

@media (max-width: 900px) {
  .footer__grid {
    grid-template-columns: 1fr 1fr;
  }

  .footer__contact {
    grid-column: 1 / -1;
  }
}

@media (max-width: 520px) {
  .footer__form {
    flex-direction: column;
    align-items: stretch;
  }

  .footer__bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
